import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NotificationModal = ({ 
  isOpen, 
  type = 'success', // 'success', 'error', 'warning', 'info'
  title, 
  message, 
  details = null,
  onClose,
  showCloseButton = true,
  autoClose = true,
  autoCloseDelay = 3000
}) => {
  const handleCopy = (text, event) => {
    navigator.clipboard.writeText(text).then(() => {
      // Mostrar feedback visual
      if (event?.target) {
        const button = event.target.closest('button');
        if (button) {
          const originalHTML = button.innerHTML;
          button.innerHTML = '<Icon name="Check" size={16} />';
          button.classList.add('bg-success');
          setTimeout(() => {
            button.innerHTML = originalHTML;
            button.classList.remove('bg-success');
          }, 2000);
        }
      }
    });
  };
  React.useEffect(() => {
    if (isOpen && autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseDelay, onClose]);

  if (!isOpen) return null;

  const getIconConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: 'CheckCircle',
          color: 'var(--color-success)',
          bgColor: 'bg-success/10',
          borderColor: 'border-success/20'
        };
      case 'error':
        return {
          icon: 'XCircle',
          color: 'var(--color-destructive)',
          bgColor: 'bg-destructive/10',
          borderColor: 'border-destructive/20'
        };
      case 'warning':
        return {
          icon: 'AlertTriangle',
          color: 'var(--color-warning)',
          bgColor: 'bg-warning/10',
          borderColor: 'border-warning/20'
        };
      case 'info':
        return {
          icon: 'Info',
          color: 'var(--color-primary)',
          bgColor: 'bg-primary/10',
          borderColor: 'border-primary/20'
        };
      default:
        return {
          icon: 'Info',
          color: 'var(--color-primary)',
          bgColor: 'bg-primary/10',
          borderColor: 'border-primary/20'
        };
    }
  };

  const iconConfig = getIconConfig();

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className={`bg-card border ${iconConfig.borderColor} rounded-lg shadow-elevation-3 max-w-md w-full mx-4 animate-in slide-in-from-bottom-4 duration-300`}>
        {/* Header */}
        <div className={`${iconConfig.bgColor} p-6 rounded-t-lg`}>
          <div className="flex items-center space-x-3">
            <div className={`w-12 h-12 ${iconConfig.bgColor} rounded-full flex items-center justify-center`}>
              <Icon name={iconConfig.icon} size={24} color={iconConfig.color} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-foreground">{title}</h3>
              {message && (
                <div className="text-sm text-muted-foreground mt-1">
                  {message.split('\n').map((line, index) => (
                    <p key={index} className={index > 0 ? 'mt-1' : ''}>
                      {line}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        {details && (
          <div className="p-6 pt-4">
            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3 text-sm">Detalhes:</h4>
              <div className="space-y-2 text-sm">
                {details.map((detail, index) => (
                  <div key={index} className="flex justify-between items-center gap-2">
                    <span className="text-muted-foreground">{detail.label}:</span>
                    {detail.isCopyable ? (
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground truncate max-w-[200px]">{detail.value}</span>
                        <button
                          onClick={(e) => handleCopy(detail.value, e)}
                          className="p-1 hover:bg-primary/10 rounded text-primary transition-colors"
                          title="Copiar link"
                        >
                          <Icon name="Copy" size={16} color="var(--color-primary)" />
                        </button>
                      </div>
                    ) : (
                      <span className="font-medium text-foreground">{detail.value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        {showCloseButton && (
          <div className="flex items-center justify-end p-6 pt-0">
            <Button 
              variant="outline" 
              onClick={onClose}
              className="min-w-[100px]"
            >
              Fechar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationModal;
